import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Divider
} from '@mui/material';

const TermsAndConditionsDialog = ({ open, onClose, onAccept }) => {
    const handleAccept = () => {
        onAccept();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                    Consent for Contact
                </Typography>
            </DialogTitle>

            <Divider />

            <DialogContent dividers sx={{ pt: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" paragraph>
                        The following terms apply in addition to our standard Terms and Conditions.
                    </Typography>
                </Box>

                {/* Section 1 */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.dark">
                        1. Consent to Be Contacted
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                        By registering for and/or using our services, you expressly consent to us, <strong>Shyena Tech Yarns Pvt, Ltd. & Shyena AI Labs Pvt. Ltd.
                        </strong>, contacting you on the telephone number(s) you have provided
                        during registration or subsequently updated in your account profile (the "Registered
                        Number(s)").
                    </Typography>
                </Box>

                {/* Section 2 */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.dark">
                        2. Purpose of Contact and Confirmation of Non-Marketing
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                        The purpose of this contact is strictly limited to non-commercial, service-improvement,
                        and feature-related activities. These calls are explicitly not for general marketing or
                        unsolicited commercial communications (UCC).
                    </Typography>
                    <Typography variant="body1" paragraph>
                        The permitted purposes include:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" paragraph>
                            Discussing future features, services, or product enhancements that are under
                            development or planning.
                        </Typography>
                        <Typography component="li" variant="body1" paragraph>
                            Conducting surveys, interviews, or feedback sessions related to the potential
                            development or improvement of our services.
                        </Typography>
                        <Typography component="li" variant="body1" paragraph>
                            <strong>Conversation Voice Call for Feature Demo:</strong> Initiating a voice call to
                            conduct a live demonstration of a future feature or service.
                        </Typography>
                        <Typography component="li" variant="body1" paragraph>
                            <strong>Setting Up a Demo:</strong> Scheduling or coordinating a future date/time for
                            a live feature demonstration.
                        </Typography>
                    </Box>
                </Box>

                {/* Section 3 */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.dark">
                        3. Method and Frequency of Contact
                    </Typography>
                    <Typography variant="body1" paragraph>
                        <strong>Method:</strong> Contact may be made via live phone calls, email, or other
                        communication channels provided during registration.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        <strong>Frequency:</strong> The Company will make reasonable efforts to limit the
                        frequency of such calls to avoid nuisance and to respect your time.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        <strong>Cost:</strong> You acknowledge that standard call rates from your
                        telecommunications provider may apply to you for receiving these calls. The Company is
                        not responsible for these charges.
                    </Typography>
                </Box>

                {/* Section 4 */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.dark">
                        4. Duration of Consent
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                        This consent remains valid for the duration that your account is active and for a period
                        of 90 days following the deactivation or termination of your account, unless explicitly
                        revoked by you earlier as per Clause 5.
                    </Typography>
                </Box>

                {/* Section 5 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.dark">
                        5. Right to Opt-Out (Revocation of Consent)
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                        You have the right to revoke this specific consent and opt-out of receiving calls for
                        future feature discussions and demos at any time by:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" paragraph>
                            Updating your communication preferences in your user account settings under the
                            section titled "Feature Discussion Calls and Demos."
                        </Typography>
                        <Typography component="li" variant="body1" paragraph>
                            Sending an email to <strong>support@yourcompany.com</strong> with the subject line
                            "OPT-OUT FEATURE CALLS AND DEMOS."
                        </Typography>
                        <Typography component="li" variant="body1" paragraph>
                            Making an oral request to the representative during a call, which will be processed
                            within 10 business days.
                        </Typography>
                    </Box>
                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                        <strong>Note:</strong> Opting out of calls for future feature discussions and demos does
                        not affect our right to contact you regarding service-related, transactional, billing,
                        security, or mandatory legal/regulatory matters.
                    </Typography>
                </Box>

                {/* Section 6 */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        By accepting these terms, you acknowledge that you have read, understood, and agree to
                        be contacted by the Company for the purposes outlined above.
                    </Typography>
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Button onClick={onClose} variant="outlined" size="large" sx={{ minWidth: 120 }}>
                    Cancel
                </Button>
                <Button onClick={handleAccept} variant="contained" size="large" sx={{ minWidth: 150 }}>
                    Accept Terms
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TermsAndConditionsDialog;
